import { useEffect, useState } from "react";
import api from "@/services/api";
import { supabase } from "@/integrations/supabase/client";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PageHeader from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BookOpen, Plus, Trash2, GraduationCap, Video, Upload } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useCourses, Course } from "@/hooks/use-courses";
import { cn } from "@/lib/utils";

const Settings = () => {
  const { courses, loading, refetch } = useCourses();
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [tab, setTab] = useState<"courses" | "video">("courses");

  const [videoUrl, setVideoUrl] = useState("");
  const [videoLoading, setVideoLoading] = useState(true);
  const [savingVideo, setSavingVideo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Supabase storage rejects keys with spaces/special chars.
  const safeName = (name: string) => name.replace(/[^a-zA-Z0-9.\-_]/g, "_").replace(/_+/g, "_");

  // Keep in sync with the bucket's file_size_limit (Supabase default is 50 MB).
  const MAX_VIDEO_MB = 50;

  const uploadVideo = async (file: File) => {
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast({
        title: "Video too large",
        description: `This file is ${(file.size / 1024 / 1024).toFixed(1)} MB. Max allowed is ${MAX_VIDEO_MB} MB — compress it, or paste a YouTube/Vimeo/hosted link instead.`,
        variant: "destructive",
      });
      return;
    }
    setUploadingVideo(true);
    try {
      const path = `${Date.now()}_${safeName(file.name)}`;
      const { error: upErr } = await supabase.storage.from("exam-videos").upload(path, file, {
        upsert: true,
        contentType: file.type || "video/mp4",
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("exam-videos").getPublicUrl(path);
      const url = pub?.publicUrl || "";
      // Persist immediately so it's live for students.
      const res = await api.put("/exam-video", { url });
      setVideoUrl(res.data?.url || url);
      toast({ title: "Video uploaded", description: "Students must watch this video before starting an exam." });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message || "Could not upload the video.", variant: "destructive" });
    } finally {
      setUploadingVideo(false);
    }
  };

  useEffect(() => {
    api.get("/exam-video")
      .then((res) => setVideoUrl(res.data?.url || ""))
      .catch(() => {})
      .finally(() => setVideoLoading(false));
  }, []);

  const saveVideo = async () => {
    setSavingVideo(true);
    try {
      const res = await api.put("/exam-video", { url: videoUrl.trim() });
      setVideoUrl(res.data?.url || "");
      toast({
        title: "Saved",
        description: res.data?.url
          ? "Students must watch this video before starting an exam."
          : "Exam video cleared — no video is required.",
      });
    } catch (e: any) {
      toast({ title: "Could not save", description: e.message, variant: "destructive" });
    } finally {
      setSavingVideo(false);
    }
  };

  const addCourse = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // Block case-insensitive duplicates before calling the API.
    if (courses.some((c) => c.name.trim().toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: "Already added", description: `“${trimmed}” is already in the list.`, variant: "destructive" });
      return;
    }
    setAdding(true);
    try {
      await api.post("/courses", { name: trimmed });
      setName("");
      toast({ title: "Course added", description: `“${trimmed}” is now available in the qualification dropdown.` });
      refetch();
    } catch (e: any) {
      toast({ title: "Could not add course", description: e.message, variant: "destructive" });
    } finally {
      setAdding(false);
    }
  };

  const isDuplicate = name.trim().length > 0 &&
    courses.some((c) => c.name.trim().toLowerCase() === name.trim().toLowerCase());

  const deleteCourse = async (course: Course) => {
    setDeletingId(course.id);
    try {
      await api.delete(`/courses/${course.id}`);
      toast({ title: "Course removed", description: `“${course.name}” was deleted.` });
      refetch();
    } catch (e: any) {
      toast({ title: "Could not delete", description: e.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const navItem = (id: "courses" | "video", icon: React.ReactNode, label: string) => (
    <button
      onClick={() => setTab(id)}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
        tab === id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted",
      )}
    >
      {icon} {label}
    </button>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Settings" />

        <Card className="glass-premium overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-[220px_1fr]">
              {/* Left nav */}
              <div className="space-y-1 border-b border-border p-3 md:border-b-0 md:border-r">
                {navItem("courses", <BookOpen className="h-4 w-4" />, "Courses")}
                {navItem("video", <Video className="h-4 w-4" />, "Exam Video")}
              </div>

              {/* Right content */}
              <div className="p-6 md:p-8">
                {tab === "courses" ? (
                  <div className="max-w-2xl space-y-5">
                    <div>
                      <h3 className="border-l-[3px] border-primary pl-3 text-base font-bold text-foreground">Courses</h3>
                      <p className="mt-1 pl-3 text-sm text-muted-foreground">Add a course to make it selectable as a qualification.</p>
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Add course</label>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCourse(); } }}
                          placeholder="e.g. B.Com, CA Inter, MBA"
                          className="h-11 bg-muted/40"
                        />
                        <Button onClick={addCourse} disabled={adding || !name.trim() || isDuplicate} className="h-11 shrink-0 gap-1.5">
                          <Plus className="h-4 w-4" /> {adding ? "Adding…" : "Add"}
                        </Button>
                      </div>
                      {isDuplicate && <p className="mt-1.5 text-xs text-red-600">“{name.trim()}” is already in the list.</p>}
                    </div>

                    <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                      {loading ? (
                        <div className="flex justify-center py-8">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        </div>
                      ) : courses.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          <GraduationCap className="mx-auto mb-2 h-7 w-7 text-muted-foreground/40" />
                          No courses yet — add your first one above.
                        </div>
                      ) : (
                        courses.map((course) => (
                          <div key={course.id} className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                            <span className="flex items-center gap-2.5 text-sm font-medium">
                              <GraduationCap className="h-4 w-4 text-primary/70" />
                              {course.name}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Delete course"
                              disabled={deletingId === course.id}
                              className="h-8 w-8 border-border/60 p-0 text-red-600 transition-all hover:border-red-600 hover:bg-red-600 hover:text-white"
                              onClick={() => deleteCourse(course)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="max-w-2xl space-y-5">
                    <div>
                      <h3 className="border-l-[3px] border-primary pl-3 text-base font-bold text-foreground">Mandatory Exam Video</h3>
                      <p className="mt-1 pl-3 text-sm text-muted-foreground">Students must watch this in full before the Start Exam button unlocks.</p>
                    </div>

                    {/* Upload a video file */}
                    <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-5">
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Upload a video</label>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <label className={cn("cursor-pointer", (uploadingVideo || videoLoading) && "pointer-events-none opacity-60")}>
                          <input
                            type="file" className="hidden" accept="video/mp4,video/webm,video/ogg"
                            disabled={uploadingVideo || videoLoading}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVideo(f); e.target.value = ""; }}
                          />
                          <span className="inline-flex items-center gap-2 rounded-lg gradient-warm px-4 py-2.5 text-sm font-semibold text-white shadow-sm">
                            <Upload className="h-4 w-4" /> {uploadingVideo ? "Uploading…" : "Choose video file"}
                          </span>
                        </label>
                        <span className="text-[11px] text-muted-foreground">MP4, WebM or OGG. Uploads are saved &amp; go live immediately.</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-border/60" />
                      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">or paste a link</span>
                      <div className="h-px flex-1 bg-border/60" />
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Video URL</label>
                      <Input
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="https://…/briefing.mp4  or  YouTube / Vimeo link"
                        className="mt-1.5 h-11 bg-muted/40"
                        disabled={videoLoading || uploadingVideo}
                      />
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        A direct file (<span className="font-mono">.mp4</span>) enforces watch-to-unlock. YouTube/Vimeo links also work but rely on a watched confirmation. Leave blank to require no video.
                      </p>
                    </div>

                    {/* Live preview */}
                    {videoUrl && /\.(mp4|webm|ogg|ogv|mov|m4v)(\?.*)?$/i.test(videoUrl) && (
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Preview</label>
                        <video src={videoUrl} controls className="w-full max-w-md rounded-lg bg-black aspect-video" />
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button onClick={saveVideo} disabled={savingVideo || videoLoading || uploadingVideo} className="gradient-warm border-0 text-white">
                        {savingVideo ? "Saving…" : "Save Video"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
